import { supabase } from '../lib/supabase';

export interface ReferredUser {
  id: string;
  name: string;
  created_at: string;
  total_sales: number;
  total_commission: number;
  sales: Array<{
    customer_name: string;
    sale_value: number;
    commission_value: number;
    sale_date: string;
  }>;
}

export async function getUserReferralCode(): Promise<string | null> {
  try {
    const user = await supabase.auth.getUser();
    if (!user.data.user) {
      return null;
    }

    // First check if user already has a referral code
    const { data: existingCode, error: existingError } = await supabase
      .from('referral_codes')
      .select('code')
      .eq('user_id', user.data.user.id)
      .maybeSingle();

    if (existingError) {
      console.error('Error checking existing code:', existingError);
      return null;
    }

    if (existingCode?.code) {
      return existingCode.code;
    }

    // Generate a new unique code with increased length and retries
    const code = await generateReferralCode(20); // Increased max attempts
    if (!code) {
      console.error('Could not generate a unique referral code after maximum attempts');
      return null;
    }
    
    // Use upsert instead of insert to handle potential race conditions
    const { error: upsertError } = await supabase
      .from('referral_codes')
      .upsert({ 
        code,
        user_id: user.data.user.id
      }, {
        onConflict: 'user_id'
      });

    if (upsertError) {
      // If upsert fails, try to get the existing code one more time
      const { data: retryCode } = await supabase
        .from('referral_codes')
        .select('code')
        .eq('user_id', user.data.user.id)
        .maybeSingle();

      if (retryCode?.code) {
        return retryCode.code;
      }

      console.error('Error upserting referral code:', upsertError);
      return null;
    }

    return code;
  } catch (err) {
    console.error('Error getting referral code:', err);
    return null;
  }
}

export async function getReferredUsers(): Promise<ReferredUser[]> {
  try {
    const user = await supabase.auth.getUser();
    if (!user.data.user) {
      return [];
    }

    // Get the current user's referral code
    const { data: referralCode } = await supabase
      .from('referral_codes')
      .select('code')
      .eq('user_id', user.data.user.id)
      .maybeSingle();

    if (!referralCode?.code) {
      return [];
    }
    
    // Get all sales and user data for this referral code
    const { data: sales, error: salesError } = await supabase
      .from('sales')
      .select(`
        sale_value,
        commission_value,
        sale_date,
        customer_name,
        referral_codes (
          user_id
        ),
        created_at
      `)
      .eq('referral_code', referralCode.code)
      .order('sale_date', { ascending: false });

    if (salesError) {
      console.error('Error fetching sales:', salesError);
      throw salesError;
    }

    // Process and aggregate sales data by user
    const usersMap = new Map<string, ReferredUser>();
    
    if (sales) {
      for (const sale of sales) {
        const userId = sale.referral_codes.user_id;
        if (!userId) continue;

        // Get user metadata
        const { data: userData } = await supabase.rpc('get_user_metadata', { user_id: userId });
        const name = userData?.name || 'Usuário sem nome';
        
        if (!usersMap.has(userId)) {
          // Initialize user with first sale
          usersMap.set(userId, {
            id: userId,
            name: name,
            created_at: sale.created_at,
            total_sales: sale.sale_value || 0,
            total_commission: sale.commission_value || 0,
            sales: [{
              customer_name: sale.customer_name,
              sale_value: sale.sale_value || 0,
              commission_value: sale.commission_value || 0,
              sale_date: sale.sale_date
            }]
          });
        } else {
          // Add to existing user's totals
          const existingUser = usersMap.get(userId)!;
          existingUser.total_sales += (sale.sale_value || 0);
          existingUser.total_commission += (sale.commission_value || 0);
          existingUser.sales.push({
            customer_name: sale.customer_name,
            sale_value: sale.sale_value || 0,
            commission_value: sale.commission_value || 0,
            sale_date: sale.sale_date
          });
        }
      }
    }

    return Array.from(usersMap.values());
  } catch (err) {
    console.error('Error getting referred users:', err);
    return [];
  }
}

async function isCodeUnique(code: string): Promise<boolean> {
  const { data } = await supabase
    .from('referral_codes')
    .select('code')
    .eq('code', code)
    .maybeSingle();
  
  return !data;
}

async function generateReferralCode(maxAttempts = 20): Promise<string | null> {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const length = 12; // Increased length for more unique combinations
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    let code = '';
    // Use crypto.getRandomValues for better randomness
    const randomBuffer = new Uint32Array(length);
    crypto.getRandomValues(randomBuffer);
    
    for (let i = 0; i < length; i++) {
      code += chars.charAt(randomBuffer[i] % chars.length);
    }
    
    // Check if the generated code is unique
    if (await isCodeUnique(code)) {
      return code;
    }
  }
  
  return null; // Could not generate a unique code after max attempts
}